// ============================================================================
// marketFeed.ts — ЛЕНТА ЗАЯВОК МАСТЕРА (hydra.market) + взятие через биллинг.
//
// ТОЛЬКО существующие API (никаких новых proto/полей/моков):
//   • заявки — документ mkt_requests через MetadataService.ListTableData
//     (серверные отборы + сортировка; контакты маскирует СЕРВЕР: до взятия
//     phone/address = «скрыт до взятия», после — только взявшему мастеру);
//   • взятие — UpdateTableRow с contact_revealed=true: сервер (internal/
//     markettake) атомарно проверяет свободность, списывает стоимость с
//     личного счёта и ставит master_id/status. Клиент НЕ дублирует финансы
//     и НЕ отправляет взятие в офлайн-очередь (деньги без связи не проводятся);
//   • баланс — BillingService.GetMyBillingAccount (личный счёт мастера).
//
// ГЕО-ОТБОР (каноническая схема v122):
//   • страна/регион/город/район — ссылки на справочники ядра (не маскируются);
//   • расстояние — Haversine от центра отбора; учитывается более строгий из
//     радиусов (мастера/отбора и заявки). Точные координаты заявки сервер
//     скрывает до оплаченного взятия (mask.go), поэтому до взятия расстояние
//     «неизвестно» и заявка НЕ отсекается — как и на сервере (geo_match.go);
//     после взятия координаты раскрываются и расстояние показывается в карточке;
//   • правила отбора повторяют серверные: неизвестное измерение не отсекает,
//     отсекает только явное несовпадение ссылок или выход за радиус.
//
// ОГРАНИЧЕНИЯ ДАННЫХ (зафиксировано, не подменяется моком):
//   • гейт телефона — серверный (markettake/phone.go): ListTableData
//     аннотирует строки phone_verified/phone_unverified/phone_verification_hint.
//     Неподтверждённый телефон блокирует взятие и раскрытие контактов; UI
//     показывает это состояние и НЕ подменяет статус клиентским «проверено».
// ============================================================================

import type { TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { readThrough } from '@/lib/offlineCache';
import { isEffectivelyOffline } from '@/lib/offlineFlag';
import {
  errorCode,
  friendlyError,
  isPermissionDeniedError,
  isTransportError,
  logSwallowed,
} from '@/lib/errors';
import {
  MARKET_REQUEST_TABLE,
  cellsOf,
  loadMarketCategories,
  loadMarketStatusLabels,
  type MarketCategory,
  type MarketRequest,
} from '@/lib/market';
import {
  EMPTY_MARKET_GEO,
  marketGeoFilter,
  marketGeoFilterActive,
  marketGeoFromCells,
  marketGeoMatches,
  type MarketGeoRow,
  type MarketGeoValue,
} from '@/lib/marketGeo';

/** Сколько заявок читает лента за один запрос (мобильный экран, без виртуализации). */
export const MARKET_FEED_PAGE_SIZE = 60;

export type MarketFeedScope = 'feed' | 'mine';

/**
 * Отборы ленты мастера. Все поля — данные интерфейса (SSoT значений — строки
 * формы): серверные отборы уходят в ListTableData, остальные применяются в
 * памяти по тем же правилам на каждой странице (см. marketFeedMatchesFilters).
 */
export interface MarketFeedFilters {
  categoryId: string;
  /** Услуга (mkt_services); пусто — все услуги категории. */
  serviceId: string;
  /** Поиск по номеру, описанию, адресу и названию категории. */
  query: string;
  /** Стоимость взятия: пусто — граница не задана. */
  priceMin: string;
  priceMax: string;
  /** Минимальный рейтинг уже назначенного мастера (пусто — без ограничения). */
  ratingMin: string;
  /**
   * Срочность: заявки не старше N часов (пусто — без ограничения).
   * В поставке hydra.market нет отдельного реквизита срочности, поэтому
   * срочность определяется детерминированно — временем публикации заявки.
   */
  urgencyHours: string;
  onlyNew: boolean;
  /** Гео-отбор: страна/регион/город/район, центр и радиус расстояния. */
  geo: MarketGeoValue;
}

export const EMPTY_MARKET_FEED_FILTERS: MarketFeedFilters = {
  categoryId: '',
  serviceId: '',
  query: '',
  priceMin: '',
  priceMax: '',
  ratingMin: '',
  urgencyHours: '',
  onlyNew: true,
  geo: { ...EMPTY_MARKET_GEO },
};

function textOf(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function optionalNumberText(value: unknown): string {
  const raw = textOf(value).replace(',', '.');
  if (!raw) return '';
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? raw : '';
}

/**
 * Нормализация отборов из ненадёжного источника (сохранённые настройки):
 * только известные поля, числовые значения — числами, пустое — пустым.
 * Отсутствующее поле = отбор не задан (никаких подстановок «по умолчанию»).
 */
export function normalizeMarketFeedFilters(value: unknown): MarketFeedFilters {
  const raw = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const geoRaw = (raw.geo && typeof raw.geo === 'object' ? raw.geo : {}) as Record<string, unknown>;
  const priceMin = optionalNumberText(raw.priceMin);
  const priceMax = optionalNumberText(raw.priceMax);
  const rating = optionalNumberText(raw.ratingMin);
  const ratingValue = rating === '' ? NaN : Number(rating);
  const urgency = optionalNumberText(raw.urgencyHours);
  const urgencyValue = urgency === '' ? NaN : Number(urgency);
  return {
    categoryId: textOf(raw.categoryId),
    serviceId: textOf(raw.serviceId),
    query: textOf(raw.query),
    // Диапазон цены: перепутанные границы меняются местами (детерминированно).
    priceMin: priceMin !== '' && priceMax !== '' && Number(priceMin) > Number(priceMax)
      ? priceMax
      : priceMin,
    priceMax: priceMin !== '' && priceMax !== '' && Number(priceMin) > Number(priceMax)
      ? priceMin
      : priceMax,
    // Рейтинг 0/вне диапазона 0..5 — ограничение не задано (а не «отсечь всё»).
    ratingMin: Number.isFinite(ratingValue) && ratingValue > 0 && ratingValue <= 5
      ? rating
      : '',
    // Срочность: только положительное число часов.
    urgencyHours: Number.isFinite(urgencyValue) && urgencyValue > 0 ? urgency : '',
    onlyNew: raw.onlyNew === undefined ? true : raw.onlyNew === true || raw.onlyNew === 'true',
    geo: {
      countryId: textOf(geoRaw.countryId),
      regionId: textOf(geoRaw.regionId),
      cityId: textOf(geoRaw.cityId),
      areaId: textOf(geoRaw.areaId),
      lat: optionalNumberText(geoRaw.lat),
      lon: optionalNumberText(geoRaw.lon),
      radiusKm: optionalNumberText(geoRaw.radiusKm),
    },
  };
}

/** Есть ли хоть один активный отбор (для подписи/сброса). */
export function marketFeedFiltersActive(filters: MarketFeedFilters): boolean {
  return marketFeedActiveFiltersCount(filters) > 0;
}

/** Количество активных отборов (для бейджа панели фильтров). */
export function marketFeedActiveFiltersCount(filters: MarketFeedFilters): number {
  let count = 0;
  if (filters.categoryId) count += 1;
  if (filters.serviceId) count += 1;
  if (filters.query.trim()) count += 1;
  if (filters.priceMin || filters.priceMax) count += 1;
  if (filters.ratingMin) count += 1;
  if (filters.urgencyHours) count += 1;
  if (marketGeoFilterActive(marketGeoFilter(filters.geo))) count += 1;
  if (filters.onlyNew) count += 1;
  return count;
}

/** Заявка ленты: те же серверные реквизиты + вычисленные клиентом признаки. */
export interface MarketFeedItem extends MarketRequest {
  /** take_price как число; null — значение пустое или не число (не подменяем нулём). */
  takePriceValue: number | null;
  /** Услуга заявки (service_id; пусто — не указана). */
  serviceId: string;
  /** Взял текущий пользователь (master_id = текущий пользователь). */
  isMine: boolean;
  /** Уже взята/в работе/завершена, но не текущим пользователем. */
  isTakenByOther: boolean;
  /** Гео строки: ссылки и (если раскрыты) координаты/радиус. */
  geo: MarketGeoRow;
  /** Подписи гео для карточки (пусто — не задано). */
  countryName: string;
  regionName: string;
  cityName: string;
  areaName: string;
}

export interface MasterBalance {
  balance: number;
  holds: number;
  available: number;
  currency: string;
}

/** Страница ленты: заявки + общее количество по отбору (сервер). */
export interface MarketFeedPage {
  items: MarketFeedItem[];
  total: number;
}

export type MasterBalanceFailureKind = 'disabled' | 'offline' | 'permission' | 'error';

export type MasterBalanceResult =
  | { ok: true; balance: MasterBalance }
  | { ok: false; kind: MasterBalanceFailureKind; message: string };

export type TakeFailureKind =
  | 'already-taken'
  | 'phone-unverified'
  | 'no-money'
  | 'billing-disabled'
  | 'offline'
  | 'permission'
  | 'error';

export type TakeResult =
  | { ok: true; already: boolean }
  | { ok: false; kind: TakeFailureKind; message: string };

/** Неуспешный результат взятия (для явного сужения типа в UI). */
export type TakeFailure = Extract<TakeResult, { ok: false }>;

export function isTakeFailure(result: TakeResult): result is TakeFailure {
  return result.ok === false;
}

/** Успешный/неуспешный баланс — явные предикаты вместо сужения по `ok`. */
export type MasterBalanceFailure = Extract<MasterBalanceResult, { ok: false }>;

export function isBalanceFailure(result: MasterBalanceResult): result is MasterBalanceFailure {
  return result.ok === false;
}

// ---------- Служебное -----------------------------------------------------------

function baseKey(): string {
  return useCenterStore.getState().baseSlug || 'local';
}

/** Ключ кэша чтения — отдельно по базе, пользователю, вкладке и отборам. */
export function marketFeedSourceKey(
  scope: MarketFeedScope,
  userId: string,
  filters: MarketFeedFilters,
): string {
  const category = filters.categoryId || 'all';
  const geo = filters.geo;
  const geoKey = [
    geo.countryId,
    geo.regionId,
    geo.cityId,
    geo.areaId,
    geo.lat,
    geo.lon,
    geo.radiusKm,
  ].map((part) => String(part || '').trim()).join('|');
  const extra = [
    filters.serviceId || 'all',
    filters.query.trim().toLowerCase() || '-',
    filters.priceMin || '-',
    filters.priceMax || '-',
    filters.ratingMin || '-',
    filters.urgencyHours || '-',
  ].join('|');
  return `market:feed:${baseKey()}:${scope}:${userId}:${category}:${filters.onlyNew ? 'new' : 'all'}:${extra}:${geoKey}`;
}

function isTrue(value: string | undefined): boolean {
  const s = String(value ?? '').trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'да';
}

function numberOf(value: string | undefined): number | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function isBillingDisabledMessage(message: string): boolean {
  const s = message.toLowerCase();
  return (
    s.includes('биллинг не включён')
    || s.includes('внутренние операции выключены')
    || s.includes('money=false')
    || s.includes('денежный контур')
  );
}

// ---------- Чтение заявок --------------------------------------------------------

/** Подписи категорий/статусов — общие для чтения ленты и живых событий. */
export interface MarketFeedAuxiliary {
  categories: Map<string, MarketCategory>;
  statusLabels: Record<string, string>;
}

async function loadAuxiliary(): Promise<MarketFeedAuxiliary> {
  let categories = new Map<string, MarketCategory>();
  let statusLabels: Record<string, string> = {};
  try {
    const list = await loadMarketCategories();
    categories = new Map(list.map((c) => [c.id, c]));
  } catch (e) {
    logSwallowed(e, 'Лента мастера: категории для названий заявок');
  }
  try {
    statusLabels = await loadMarketStatusLabels();
  } catch (e) {
    logSwallowed(e, 'Лента мастера: подписи статусов заявок');
  }
  return { categories, statusLabels };
}

/**
 * Вспомогательные подписи для ЖИВЫХ событий (категории/статусы). Читаются из
 * кэша чтения (readThrough) — в офлайне доступны последние сохранённые.
 */
export function loadMarketFeedAuxiliary(): Promise<MarketFeedAuxiliary> {
  return loadAuxiliary();
}

export function marketFeedItemFromRow(
  row: TableDataRow,
  userId: string,
  categories: Map<string, MarketCategory>,
  statusLabels: Record<string, string>,
): MarketFeedItem {
  const cells = cellsOf(row);
  const categoryId = cells.get('category_id') || '';
  const masterId = cells.get('master_id') || '';
  const status = cells.get('status') || '';
  const contactRevealed = isTrue(cells.get('contact_revealed'));
  const takePriceRaw = cells.get('take_price') || '';
  const isMine = !!userId && masterId !== '' && masterId === userId;
  // «Занята» — оплаченное взятие (contact_revealed) ИЛИ статус уже не «новая»:
  // сервер ставит contact_revealed и status=taken одной транзакцией, но
  // статус мог быть изменён позже (в работе/выполнена).
  const occupied = contactRevealed || (status !== '' && status !== 'new');
  return {
    id: row.id,
    number: cells.get('number') || '',
    categoryId,
    serviceId: cells.get('service_id') || '',
    categoryName:
      cells.get('category_id_presentation')
      || categories.get(categoryId)?.name
      || '',
    masterId,
    masterName: cells.get('master_id_presentation') || cells.get('master_name') || '',
    status,
    statusLabel: cells.get('status_name') || statusLabels[status] || status,
    descr: cells.get('descr') || '',
    address: cells.get('address') || '',
    phone: cells.get('phone') || '',
    createdAt: cells.get('created_at') || '',
    createdBy: cells.get('created_by') || '',
    takePrice: takePriceRaw,
    takePriceValue: numberOf(takePriceRaw),
    contactRevealed,
    isMarked: isTrue(cells.get('is_marked')),
    phoneVerified: isTrue(cells.get('phone_verified')),
    phoneUnverified: isTrue(cells.get('phone_unverified')),
    phoneVerificationHint: cells.get('phone_verification_hint') || '',
    masterCatalogId: cells.get('master_catalog_id') || '',
    masterRating: numberOf(cells.get('master_rating')),
    masterReviewsCount: numberOf(cells.get('master_reviews_count')),
    isMine,
    isTakenByOther: occupied && !isMine,
    geo: marketGeoFromCells(cells),
    countryName: cells.get('country_id_presentation') || '',
    regionName: cells.get('region_id_presentation') || '',
    // Карточные события ленты несут только текстовый город/район (без ссылок) —
    // это подпись для карточки, фильтрация идёт по каноническим city_id/area_id.
    cityName: cells.get('city_id_presentation') || cells.get('city') || '',
    areaName: cells.get('area_id_presentation') || cells.get('district') || '',
  };
}

/** Время создания заявки в мс (0 — значение не разобрано/пустое). */
export function marketFeedCreatedAtMs(raw: string): number {
  const value = String(raw || '').trim();
  if (!value) return 0;
  const direct = Date.parse(value);
  if (Number.isFinite(direct)) return direct;
  // Go-формат с именованной зоной («2026-10-23 10:00:00 +0500 +05») браузер
  // может не разобрать — вытаскиваем компоненты и смещение вручную.
  const m = value.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.(\d+))?(?:\s*([+-]\d{2}):?(\d{2}))?/);
  if (!m) return 0;
  const fraction = m[3] ? `.${m[3].slice(0, 3).padEnd(3, '0')}` : '';
  const zone = m[4] ? `${m[4]}:${m[5] || '00'}` : 'Z';
  const parsed = Date.parse(`${m[1]}T${m[2]}${fraction}${zone}`);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Поля карточки, по которым применяются отборы (достаточно для событий). */
export type MarketFeedFilterable = Pick<
  MarketFeedItem,
  | 'status'
  | 'categoryId'
  | 'serviceId'
  | 'number'
  | 'descr'
  | 'address'
  | 'categoryName'
  | 'takePriceValue'
  | 'masterRating'
  | 'createdAt'
  | 'geo'
  | 'isMarked'
  | 'isMine'
>;

/**
 * Соответствует ли заявка отборам ленты. ОДНИ правила для серверной загрузки
 * и живых событий:
 *  • категория/услуга — известно только явное несовпадение (события могут
 *    нести частичную строку: неизвестное измерение не отсекает);
 *  • «только новые» — только для свободной ленты;
 *  • цена — при заданной границе заявка без стоимости не показывается
 *    (проверить ограничение нечем);
 *  • рейтинг мастера — неизвестный рейтинг не отсекает (заявка без мастера);
 *  • срочность — отсекаются только заявки с известным временем старше окна;
 *  • гео — правила сервера (marketGeoMatches).
 */
export function marketFeedMatchesFilters(
  item: MarketFeedFilterable,
  scope: MarketFeedScope,
  filters: MarketFeedFilters,
): boolean {
  if (item.isMarked) return false;
  if (scope === 'mine' && !item.isMine) return false;
  if (filters.categoryId && item.categoryId && item.categoryId !== filters.categoryId) return false;
  if (filters.serviceId && item.serviceId && item.serviceId !== filters.serviceId) return false;
  if (scope === 'feed' && filters.onlyNew && item.status !== 'new') return false;

  const query = filters.query.trim().toLowerCase();
  if (query) {
    const haystack = [item.number, item.descr, item.address, item.categoryName]
      .join(' ')
      .toLowerCase();
    // Каждое слово запроса должно найтись (порядок слов не важен).
    for (const word of query.split(/\s+/)) {
      if (word && !haystack.includes(word)) return false;
    }
  }

  if (filters.priceMin || filters.priceMax) {
    if (item.takePriceValue === null) return false;
    if (filters.priceMin && item.takePriceValue < Number(filters.priceMin)) return false;
    if (filters.priceMax && item.takePriceValue > Number(filters.priceMax)) return false;
  }

  if (filters.ratingMin) {
    const min = Number(filters.ratingMin);
    if (item.masterRating !== null && Number.isFinite(min) && item.masterRating < min) return false;
  }

  if (filters.urgencyHours) {
    const hours = Number(filters.urgencyHours);
    const createdMs = marketFeedCreatedAtMs(item.createdAt);
    if (Number.isFinite(hours) && hours > 0 && createdMs > 0) {
      if (Date.now() - createdMs > hours * 3600_000) return false;
    }
  }

  const geoFilter = marketGeoFilter(filters.geo);
  if (marketGeoFilterActive(geoFilter) && !marketGeoMatches(geoFilter, item.geo)) return false;
  return true;
}

/** Отборы, которые ListTableData не умеет применять (нужен проход по страницам). */
function requiresPageScan(filters: MarketFeedFilters): boolean {
  return Boolean(
    filters.query.trim()
    || filters.priceMin
    || filters.priceMax
    || filters.ratingMin
    || filters.urgencyHours
    || marketGeoFilterActive(marketGeoFilter(filters.geo)),
  );
}

async function fetchFeedItems(
  scope: MarketFeedScope,
  userId: string,
  filters: MarketFeedFilters,
  aux: MarketFeedAuxiliary,
): Promise<{ items: MarketFeedItem[]; total: number }> {
  const serverFilters: Array<{ column: string; value: string }> = [];
  if (filters.categoryId) serverFilters.push({ column: 'category_id', value: filters.categoryId });
  if (filters.serviceId) serverFilters.push({ column: 'service_id', value: filters.serviceId });
  if (filters.onlyNew && scope === 'feed') serverFilters.push({ column: 'status', value: 'new' });
  if (scope === 'mine') {
    if (!userId) throw new Error('Не определён текущий пользователь — войдите заново');
    serverFilters.push({ column: 'master_id', value: userId });
  }
  const requestPage = async (page: number, pageSize: number) => {
    const res = await getSDK().metadata.listTableData({
      schema: 'public',
      table: MARKET_REQUEST_TABLE,
      pageSize,
      page,
      includeMarked: false,
      orderBy: 'created_at',
      orderDesc: true,
      filters: serverFilters,
    });
    return {
      rows: res.rows,
      total: Number(res.totalCount || 0),
    };
  };
  const toItem = (row: TableDataRow) =>
    marketFeedItemFromRow(row, userId, aux.categories, aux.statusLabels);

  if (!requiresPageScan(filters)) {
    const res = await requestPage(0, MARKET_FEED_PAGE_SIZE);
    // Серверные отборы уже применены; предикат — страховка и общий путь.
    const items = res.rows
      .map(toItem)
      .filter((item: MarketFeedItem) => marketFeedMatchesFilters(item, scope, filters));
    return { items, total: res.total };
  }

  // Отборы в памяти (текст/цена/рейтинг/срочность/гео): серверный
  // ListTableData либо не умеет условие, либо «неизвестное не отсекает».
  // Читаем свежие страницы, добирая до заполнения страницы ленты.
  const matched: MarketFeedItem[] = [];
  const seen = new Set<string>();
  const scanPageSize = 100;
  // Защита от «всей таблицы»: отбор идёт по последним заявкам, а не по архиву.
  const maxScanRows = 1000;
  let scanned = 0;
  let serverTotal = Number.POSITIVE_INFINITY;
  for (let page = 0; matched.length < MARKET_FEED_PAGE_SIZE && scanned < maxScanRows; page++) {
    const { rows, total } = await requestPage(page, scanPageSize);
    if (total > 0) serverTotal = total;
    if (rows.length === 0) break;
    scanned += rows.length;
    for (const row of rows) {
      if (matched.length >= MARKET_FEED_PAGE_SIZE) break;
      // Смещение страниц может дать дубль, если между запросами появилась
      // новая заявка — одна карточка не должна попасть в ленту дважды.
      if (seen.has(row.id)) continue;
      const item = toItem(row);
      if (marketFeedMatchesFilters(item, scope, filters)) {
        seen.add(row.id);
        matched.push(item);
      }
    }
    if (rows.length < scanPageSize) break;
    if (scanned >= serverTotal) break;
  }
  return { items: matched, total: matched.length };
}

/**
 * Заявки мастера: scope='feed' — свободные (и история, если «только новые»
 * выключен), scope='mine' — взятые текущим пользователем. Сортировка —
 * по свежести (created_at DESC), сервер её и выполняет.
 */
export async function loadMarketFeed(
  scope: MarketFeedScope,
  userId: string,
  filters: MarketFeedFilters,
): Promise<MarketFeedPage> {
  if (!userId) throw new Error('Не определён текущий пользователь — войдите заново');
  const key = marketFeedSourceKey(scope, userId, filters);
  const cached = await readThrough<MarketFeedPage>(key, async () => {
    const aux = await loadAuxiliary();
    return fetchFeedItems(scope, userId, filters, aux);
  });
  return cached.data;
}

// ---------- Баланс мастера --------------------------------------------------------

/** Личный счёт мастера: баланс/холды/доступно. Никаких «нулей» при ошибке. */
export async function loadMasterBalance(): Promise<MasterBalanceResult> {
  if (isEffectivelyOffline()) {
    return { ok: false, kind: 'offline', message: 'Нет связи с сервером — баланс недоступен' };
  }
  try {
    const res = await getSDK().billing.getMyBillingAccount({});
    const bal = res.balance;
    if (!bal) {
      return { ok: false, kind: 'error', message: 'Сервер не вернул баланс личного счёта' };
    }
    return {
      ok: true,
      balance: {
        balance: Number(bal.balance),
        holds: Number(bal.holds),
        available: Number(bal.available),
        currency: String(bal.currency || ''),
      },
    };
  } catch (e) {
    const message = friendlyError(e).trim() || 'Не удалось загрузить баланс';
    if (isTransportError(e)) {
      return { ok: false, kind: 'offline', message };
    }
    if (isPermissionDeniedError(e)) {
      return { ok: false, kind: 'permission', message };
    }
    if (isBillingDisabledMessage(message)) {
      return { ok: false, kind: 'disabled', message };
    }
    return { ok: false, kind: 'error', message };
  }
}

// ---------- Взятие заявки ----------------------------------------------------------

function classifyTakeError(e: unknown): TakeResult {
  const message = friendlyError(e).trim() || 'Не удалось взять заявку';
  if (isTransportError(e)) {
    return { ok: false, kind: 'offline', message };
  }
  // Серверный гейт телефона (FailedPrecondition): заявка не взята, деньги
  // не списаны. Сообщение сервера сохраняем как есть (SSoT), UI показывает
  // отдельное состояние «телефон не подтверждён» без второго тоста.
  if (/phone_unverified/i.test(message)
    || /(телефон|номер)[^.]*не\s+подтвержд/i.test(message)) {
    return { ok: false, kind: 'phone-unverified', message };
  }
  const code = errorCode(e);
  if (code === 6 || /уже взята/i.test(message)) {
    return { ok: false, kind: 'already-taken', message };
  }
  if (code === 9 && /недостаточно средств/i.test(message)) {
    return { ok: false, kind: 'no-money', message };
  }
  if (isBillingDisabledMessage(message)) {
    return { ok: false, kind: 'billing-disabled', message };
  }
  if (isPermissionDeniedError(e)) {
    return { ok: false, kind: 'permission', message };
  }
  return { ok: false, kind: 'error', message };
}

/**
 * Взять заявку: ЕДИНСТВЕННЫЙ вызов существующего API — UpdateTableRow с
 * contact_revealed=true. Сервер перехватывает сохранение (atomic take):
 * проверка свободности → списание стоимости → master_id/status/taken.
 *
 * ВАЖНО: прямой SDK-вызов, а не dataService.update — финансовую операцию
 * НЕЛЬЗЯ отправлять в офлайн-очередь (при обрыве связи клиент получил бы
 * «успех», а заявка осталась бы свободной). Ошибки сервера возвращаются
 * вызывающему с явным видом (нет денег / уже взята / права / связь).
 */
export async function takeMarketRequest(requestId: string, userId: string): Promise<TakeResult> {
  if (!requestId) {
    return { ok: false, kind: 'error', message: 'Не указана заявка' };
  }
  if (!userId) {
    return { ok: false, kind: 'permission', message: 'Не определён текущий пользователь — войдите заново' };
  }
  if (isEffectivelyOffline()) {
    return {
      ok: false,
      kind: 'offline',
      message: 'Взятие заявки списывает деньги — требуется связь с сервером',
    };
  }
  try {
    await getSDK().metadata.updateTableRow({
      schema: 'public',
      table: MARKET_REQUEST_TABLE,
      rowId: requestId,
      values: [
        { column: 'contact_revealed', value: 'true' },
        { column: 'status', value: 'taken' },
      ],
    });
    // Проверка результата (защита от гонки): если заявку забрал другой мастер
    // между загрузкой ленты и нажатием, сервер мог обработать сохранение как
    // обычное изменение (заявка уже раскрыта). Читаем актуальное состояние:
    // успех — только если мастер = я и контакт раскрыт. Сбой контрольного
    // чтения не отменяет уже выполненную сервером операцию.
    try {
      const check = await getSDK().metadata.listTableData({
        schema: 'public',
        table: MARKET_REQUEST_TABLE,
        pageSize: 1,
        page: 0,
        includeMarked: false,
        filters: [{ column: 'id', value: requestId }],
      });
      const row = check.rows[0];
      if (row) {
        const cells = cellsOf(row);
        const owner = cells.get('master_id') || '';
        if (!isTrue(cells.get('contact_revealed')) || owner !== userId) {
          return {
            ok: false,
            kind: 'already-taken',
            message: 'Заявка уже взята другим мастером',
          };
        }
      }
    } catch (verifyError) {
      // Контрольное чтение не удалось — операция сервера уже принята;
      // не превращаем успешное взятие в ложную ошибку.
      logSwallowed(verifyError, 'Лента мастера: проверка результата взятия');
    }
    return { ok: true, already: false };
  } catch (e) {
    return classifyTakeError(e);
  }
}
